import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';

type PermissionAction = 'read' | 'add' | 'edit';

/** Rutas cuyo nombre de módulo de permisos en el backend es distinto al segmento de URL. */
const ROUTE_PERMISSION_ALIASES: Record<string, string> = {
  'charging-transactions': 'charging-sessions',
  'price-configuration': 'prices',
  'missing-price-configurations-report': 'price-missing',
};

/** Primer segmento de URL de los módulos que requieren permiso. Al agregar un módulo nuevo, registrarlo aquí. */
const PROTECTED_ROUTE_SEGMENTS = new Set<string>([
  'roles',
  'regions',
  'zones',
  'users',
  'commercial-zones',
  'commercial-conditions',
  'customer-groups',
  'connector-types',
  'postal-codes',
  'amenities',
  'vehicle-models',
  'companies',
  'customers',
  'faqs',
  'stations',
  'chargers',
  'connectors',
  'push-notifications',
  'price-configuration',
  'missing-price-configurations-report',
  'stations-map',
  'station-reviews',
  'charging-transactions',
  'reservation-policies',
  'invoices',
  'charger-notifications',
]);

const CREATE_ROUTE_SEGMENTS = new Set(['create', 'new']);
const EDIT_ROUTE_SEGMENTS = new Set(['edit']);

function getPathSegments(url: string): string[] {
  const cleanUrl = (url || '').split('?')[0].split('#')[0];

  return cleanUrl
    .split('/')
    .map((segment) => segment.trim())
    .filter((segment) => !!segment);
}

function resolvePermissionAction(segments: string[]): PermissionAction {
  if (segments.some((segment) => CREATE_ROUTE_SEGMENTS.has(segment))) {
    return 'add';
  }

  if (segments.some((segment) => EDIT_ROUTE_SEGMENTS.has(segment))) {
    return 'edit';
  }

  return 'read';
}

/**
 * Determina el permiso requerido para la ruta: usa `data.permission` si está definido;
 * si no, lo deriva de la URL como `{módulo}.{read|add|edit}` (p. ej. /users/edit/5 → users.edit).
 * Devuelve null si la ruta no está protegida.
 */
function resolveRequiredPermission(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): string | null {
  const explicitPermission = route.data?.['permission'];
  if (typeof explicitPermission === 'string' && explicitPermission.trim()) {
    return explicitPermission.trim();
  }

  const segments = getPathSegments(state.url);
  if (segments.length === 0) {
    return null;
  }

  const moduleSegment = segments[0];
  if (!PROTECTED_ROUTE_SEGMENTS.has(moduleSegment)) {
    return null;
  }

  const permissionModule = ROUTE_PERMISSION_ALIASES[moduleSegment] ?? moduleSegment;
  const action = resolvePermissionAction(segments);
  return `${permissionModule}.${action}`;
}

/**
 * Valida que el usuario tenga el permiso exacto de la ruta (`.read`, `.add` o `.edit`);
 * si no, redirige al inicio. Los listados deben ocultar los botones de alta/edición
 * cuando el usuario no tiene esos permisos.
 */
export const permissionGuard = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const requiredPermission = resolveRequiredPermission(route, state);
  if (!requiredPermission) {
    return true;
  }

  if (authService.hasPermission(requiredPermission)) {
    return true;
  }

  console.warn(`Acceso denegado. Permiso requerido: ${requiredPermission}`);
  router.navigate(['/']);
  return false;
};
